'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { memo, useCallback, useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { type InferType } from 'yup';

import { useTrackLoggedIn } from '@air/analytics';
import { useToasts } from '@air/provider-toast';
import {
  getIsSAMLEnforced,
  getLoginError,
  loginValidationSchema,
  useGetCurrentAccount,
  useLoginMutation,
  useLogoutMutation,
} from '@air/utils-auth';

import { Button, type ButtonVariants } from '~/components/Button';
import { Input } from '~/components/Input';
import { Label } from '~/components/Label';

export type LoginFormInput = InferType<typeof loginValidationSchema>;

export type LoginFormProps = {
  /** Callback when login succeeds */
  onSuccess?: () => void;
  /** Submit button size — defaults to 'large' for backwards compatibility */
  size?: ButtonVariants['size'];
};

/**
 * Email/password login form component
 */
export const LoginForm = memo(({ onSuccess, size = 'large' }: LoginFormProps) => {
  const formId = useId();
  const { showToast } = useToasts();
  const { mutate: loginMutate, isPending: isLoginPending } = useLoginMutation();
  const { handleSubmit, register } = useForm({
    resolver: yupResolver(loginValidationSchema),
  });
  const { getCurrentAccount } = useGetCurrentAccount();
  const { trackLoggedIn } = useTrackLoggedIn();
  const [isCheckingSAML, setIsCheckingSAML] = useState(false);
  const {
    logoutMutation: { mutate: logout },
  } = useLogoutMutation();

  const isSubmitting = isCheckingSAML || isLoginPending;

  const onSubmit = useCallback(
    async ({ password, email }: LoginFormInput) => {
      setIsCheckingSAML(true);

      try {
        const isSAMLEnforced = await getIsSAMLEnforced(email);

        if (isSAMLEnforced) {
          showToast('You must sign in with SAML SSO for this account', {
            timeBeforeAutoDismiss: undefined,
            type: 'assertive',
            withCloseButton: true,
          });
        } else {
          loginMutate(
            {
              password,
              username: email,
            },
            {
              onError: (error, variables) => {
                showToast(getLoginError({ email: variables.username, error }));
              },
              onSuccess: async () => {
                try {
                  const { email, firstName, id, lastName } = await getCurrentAccount();

                  trackLoggedIn({ authProvider: 'Cognito', email, firstName, id, lastName });

                  showToast('Login successful');
                  onSuccess?.();
                } catch (error) {
                  logout({});
                  showToast(getLoginError({ error }));
                }
              },
            },
          );
        }
      } catch {
        showToast('Unable to verify account. Please try again.');
      } finally {
        setIsCheckingSAML(false);
      }
    },
    [getCurrentAccount, loginMutate, onSuccess, showToast, trackLoggedIn, logout],
  );

  return (
    <form className="flex w-full flex-col" id={formId} onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-4 px-8">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${formId}-email`}>Email</Label>
          <Input id={`${formId}-email`} placeholder="you@example.com" type="email" {...register('email')} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${formId}-password`}>Password</Label>
          <Input id={`${formId}-password`} placeholder="••••••••" type="password" {...register('password')} />
        </div>
      </div>
      <div className="mt-8 h-px bg-neutral-6" />
      <div className="flex w-full flex-col p-4">
        <Button appearance="primary" disabled={isSubmitting} isLoading={isSubmitting} size={size} type="submit">
          Log in
        </Button>
      </div>
    </form>
  );
});

LoginForm.displayName = 'LoginForm';
